import { Flex, Box } from "@chakra-ui/react";
import { Modal, Col } from "antd";
import OutlineButton from "components/Button/OutlineButton";
import PrimaryButton from "components/Button/PrimaryButton";
import { useForm } from "react-hook-form";
import Field from "components/Input/Field/index";
import SelectTipoArrecadacao from "components/Input/SelectTipoArrecadacao/index";
import { PrevisaoOrcamentaria } from "core/features/previsao-orcamentaria/typings";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";
import useSavePrevisaoOrcamentaria from "core/features/previsao-orcamentaria/hooks/useSavePrevisaoOrcamentaria";
import { useEffect } from "react";
import SelectAno from "components/Input/SelectAno/index";
import { PrevisaoOrcamentariaForm } from "core/features/previsao-orcamentaria/typings/index";
import moment from "moment";
import usePrevisaoOrcamentariaParametro from "core/features/previsao-orcamentaria/hooks/usePrevisaoOrcamentariaParametro";
import { useLoading } from "ui/components/GlobalLoading";

interface ModalFormPrevisaoOrcamentariaProps {
  onClose: () => void;
  visible: boolean;
  id?: string;
  data: PrevisaoOrcamentaria | undefined;
  refetch: any;
}

const defaultValuesForm: Partial<PrevisaoOrcamentariaForm> = {
  nome: "",
  taxaInadimplenciaPrevista: undefined,
  tipoArrecadacaoId: "",
  ano: moment().format("YYYY"),
};

export default function ModalFormPrevisaoOrcamentaria({
  onClose,
  visible,
  id,
  data,
  refetch,
}: ModalFormPrevisaoOrcamentariaProps) {
  const { mutate: savePrevisao, isLoading: isLoadingSave } =
    useSavePrevisaoOrcamentaria(id);

  const {
    data: dataParametro,
    isLoading,
    isFetching,
  } = usePrevisaoOrcamentariaParametro(!data);

  const { control, handleSubmit, reset } = useForm<PrevisaoOrcamentariaForm>(
    {}
  );

  const onSubmit = async (data: PrevisaoOrcamentariaForm) => {
    savePrevisao(
      {
        ...data,
        dataInicio: moment(data.ano, "YYYY")
          .startOf("year")
          .format("YYYY-MM-DD"),
        dataFim: moment(data.ano, "YYYY").endOf("year").format("YYYY-MM-DD"),
      },
      {
        onSuccess: () => {
          onClose();
          showSuccess();
          refetch?.();
        },
        onError: (e) => {
          showErrorAxios(e);
        },
      }
    );
  };

  useEffect(() => {
    if (data) {
      reset({
        id: data.id,
        createdAt: data.createdAt,
        nome: data.nome,
        taxaInadimplenciaPrevista: data.taxaInadimplenciaPrevista,
        tipoArrecadacaoId: data.tipoArrecadacaoId,
        valorArrecadacao: data.valorArrecadacao,
        ano: moment(data.dataInicio).format("YYYY"),
      });
    }
  }, [data, reset]);

  useEffect(() => {
    if (!visible) {
      reset({
        ...defaultValuesForm,
        taxaInadimplenciaPrevista: dataParametro?.indiceMedioInadimplencia,
      });
    }
  }, [dataParametro?.indiceMedioInadimplencia, reset, visible]);

  useLoading(isLoadingSave || isLoading);
  return (
    <Modal
      centered
      destroyOnClose
      width="500px"
      visible={visible}
      onCancel={onClose}
      title="Criar previsão"
      footer={
        <Flex flex={1} justify="end" gap={4}>
          <OutlineButton onClick={onClose}>Fechar</OutlineButton>
          <PrimaryButton
            isLoading={isLoadingSave || isLoading || isFetching}
            onClick={() => handleSubmit(onSubmit)()}
          >
            Salvar
          </PrimaryButton>
        </Flex>
      }
    >
      <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Nome"
            control={control}
            name="nome"
            rules={{
              required: "Campo obrigatório",
            }}
          />
        </Box>

        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Ano"
            control={control}
            name="ano"
            render={({ field }) => <SelectAno {...field} />}
            rules={{
              required: "Campo obrigatório",
            }}
          />
        </Box>

        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Tipo de arrecadação"
            control={control}
            name="tipoArrecadacaoId"
            render={({ field }) => <SelectTipoArrecadacao {...field} />}
            rules={{
              required: "Campo obrigatório",
            }}
          />
        </Box>

        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Taxa de inadimplência prevista"
            control={control}
            name="taxaInadimplenciaPrevista"
            rules={{
              required: "Campo obrigatório",
            }}
            inputProps={{
              disabled: isLoading || isFetching,
              isLoading: isLoading || isFetching,
            }}
          />
        </Box>
      </form>
    </Modal>
  );
}
